"use client";

import { motion } from "framer-motion";
import { type LucideIcon, Sparkles, Users } from "lucide-react";
import Image from "next/image";
import { type ReactNode, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Avatar } from "@/ui/elements";
import { EASE } from "../../home/use-case-tabs/constants";
import { useStickToBottom } from "../../home/use-case-tabs/use-stick-to-bottom";
import { CountUp } from "./count-up";

/* ------------------------------------------------------------------ */
/* Types                                                              */
/* ------------------------------------------------------------------ */

export type Person = { name: string; src?: string };

export type Metric = {
  icon: LucideIcon;
  label: string;
  /** Target value the cell counts up to from 0. */
  to: number;
  /** Formats the animated number (e.g. percentage, locale string). */
  format?: (n: number) => string;
  /** Static, non-animated tail such as " / 1,240". */
  suffix?: string;
};

export type BlastStep =
  | { kind: "audience"; id: string; name: string; count: string; filters: string[] }
  | { kind: "message"; id: string; body: ReactNode; caption?: string }
  | {
      kind: "email";
      id: string;
      caption?: string;
      brand: string;
      logo: string;
      image: string;
      subject: string;
      cta: string;
    }
  | { kind: "metrics"; id: string; items: Metric[] }
  | {
      kind: "reply";
      id: string;
      direction: "incoming" | "outgoing";
      person?: Person;
      body: ReactNode;
    };

export type Campaign = { name: string; channel: string };

/* ------------------------------------------------------------------ */
/* Step renderers                                                     */
/* ------------------------------------------------------------------ */

function AudienceBlock({
  name,
  count,
  filters,
}: {
  name: string;
  count: string;
  filters: string[];
}) {
  return (
    <div className="rounded-xl border border-border bg-background p-3 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-foreground">
          <Users className="size-3.5 text-muted-foreground" />
          {name}
        </span>
        <Badge variant="outline" className="shrink-0 bg-background text-[10px] font-semibold">
          {count}
        </Badge>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {filters.map((f) => (
          <span
            key={f}
            className="inline-flex items-center rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[11px] font-medium text-foreground"
          >
            {f}
          </span>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ body, caption }: { body: ReactNode; caption?: string }) {
  return (
    <div className="w-full text-right">
      {caption ? <p className="mb-1 text-[10px] text-muted-foreground">{caption}</p> : null}
      <div className="inline-block max-w-[17rem] whitespace-pre-line rounded-2xl rounded-br-md bg-outgoing-bubble px-3 py-2 text-left text-[13px] leading-snug text-white">
        {body}
      </div>
    </div>
  );
}

function EmailPreview({
  caption,
  brand,
  logo,
  image,
  subject,
  cta,
}: {
  caption?: string;
  brand: string;
  logo: string;
  image: string;
  subject: string;
  cta: string;
}) {
  return (
    <div>
      {caption ? <p className="mb-1 text-right text-[10px] text-muted-foreground">{caption}</p> : null}
      <div className="overflow-hidden rounded-xl border border-border bg-background shadow-sm">
        <div className="flex items-center gap-2 border-b border-border px-3 py-2">
          <Image src={logo} alt={brand} width={20} height={20} className="size-4 rounded-[3px]" />
          <span className="text-[10px] font-semibold uppercase tracking-wider text-foreground">
            {brand}
          </span>
        </div>
        <Image
          src={image}
          alt=""
          width={800}
          height={350}
          className="aspect-[16/7] w-full object-cover"
        />
        <div className="space-y-2.5 p-3">
          <p className="text-[13px] font-bold leading-snug text-foreground">{subject}</p>
          <div className="space-y-1.5">
            <span className="block h-1.5 w-full rounded-full bg-muted" />
            <span className="block h-1.5 w-5/6 rounded-full bg-muted" />
            <span className="block h-1.5 w-3/4 rounded-full bg-muted" />
          </div>
          <span className="inline-block rounded-lg bg-outgoing-bubble px-3 py-1.5 text-[11px] font-semibold text-white">
            {cta}
          </span>
        </div>
      </div>
    </div>
  );
}

function MetricsBlock({ items }: { items: Metric[] }) {
  return (
    <div
      className={cn(
        "grid gap-px overflow-hidden rounded-xl border border-border bg-border shadow-sm [&>*]:bg-background",
        items.length === 3 ? "grid-cols-3" : "grid-cols-2",
      )}
    >
      {items.map(({ icon: Icon, label, to, format, suffix }) => (
        <div key={label} className="flex flex-col gap-0.5 px-3 py-2.5">
          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground">
            <Icon className="size-2.5 shrink-0" />
            {label}
          </span>
          <span className="text-[15px] font-bold tabular-nums tracking-tight text-foreground">
            <CountUp to={to} format={format} />
            {suffix ? <span className="font-medium text-muted-foreground">{suffix}</span> : null}
          </span>
        </div>
      ))}
    </div>
  );
}

function ReplyRow({
  direction,
  person,
  body,
}: {
  direction: "incoming" | "outgoing";
  person?: Person;
  body: ReactNode;
}) {
  if (direction === "outgoing") {
    return (
      <div className="w-full text-right">
        <div className="inline-block max-w-[15rem] rounded-2xl rounded-br-md bg-outgoing-bubble px-3 py-2 text-left text-[13px] leading-snug text-white">
          {body}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-end gap-2">
      {person ? <Avatar name={person.name} src={person.src} size="xs" /> : null}
      <div className="max-w-[15rem] rounded-2xl rounded-bl-md border border-border bg-incoming-bubble px-3 py-2 text-[13px] leading-snug text-foreground">
        {body}
      </div>
    </div>
  );
}

function StepRow({ step }: { step: BlastStep }) {
  switch (step.kind) {
    case "audience":
      return <AudienceBlock name={step.name} count={step.count} filters={step.filters} />;
    case "message":
      return <MessageBubble body={step.body} caption={step.caption} />;
    case "email":
      return (
        <EmailPreview
          caption={step.caption}
          brand={step.brand}
          logo={step.logo}
          image={step.image}
          subject={step.subject}
          cta={step.cta}
        />
      );
    case "metrics":
      return <MetricsBlock items={step.items} />;
    case "reply":
      return <ReplyRow direction={step.direction} person={step.person} body={step.body} />;
    default:
      return null;
  }
}

/* ------------------------------------------------------------------ */
/* Status pill — Draft → Sending → Sent, driven by what's revealed    */
/* ------------------------------------------------------------------ */

function statusFor(revealed: BlastStep[]): { label: string; className: string } {
  const sent = revealed.some((s) => s.kind === "metrics" || s.kind === "reply");
  if (sent) {
    return {
      label: "Sent",
      className: "bg-success/10 text-success",
    };
  }
  const sending = revealed.some((s) => s.kind === "message" || s.kind === "email");
  if (sending) {
    return {
      label: "Sending",
      className: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    };
  }
  return { label: "Draft", className: "bg-muted text-muted-foreground" };
}

/* ------------------------------------------------------------------ */
/* Feed card                                                          */
/* ------------------------------------------------------------------ */

export function BlastFeed({
  campaign,
  steps,
  visible,
  reduce,
  flow = false,
  className,
}: {
  campaign: Campaign;
  steps: BlastStep[];
  visible: number;
  reduce: boolean;
  flow?: boolean;
  className?: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  useStickToBottom(scrollRef, flow ? 0 : visible, reduce);

  const shown = steps.slice(0, visible);
  const status = statusFor(shown);

  return (
    <div
      className={cn(
        "flex min-h-0 flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-sm",
        flow ? "h-auto" : "h-full",
        className,
      )}
    >
      {/* Campaign header */}
      <div className="flex items-center gap-2.5 border-b border-border px-3 py-2.5">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-border bg-muted/50 text-muted-foreground">
          <Sparkles className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold text-foreground">{campaign.name}</p>
          <p className="truncate text-[11px] text-muted-foreground">{campaign.channel}</p>
        </div>
        <span
          className={cn(
            "inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium",
            status.className,
          )}
        >
          <span className="size-1.5 rounded-full bg-current" />
          {status.label}
        </span>
      </div>

      {/* Campaign feed */}
      <div
        ref={scrollRef}
        className={cn(
          "space-y-3 px-3 py-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          flow ? "" : "flex-1 overflow-y-auto",
        )}
      >
        {shown.map((step) => (
          <motion.div
            key={step.id}
            initial={reduce ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
          >
            <StepRow step={step} />
          </motion.div>
        ))}
      </div>
    </div>
  );
}
